// @ts-strict-ignore
import { MountingRailHelper } from '../helper/mounting-rail.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable } from '@angular/core';

export class TerminalBlock {
  private _articleUsageIds: string[];

  constructor(articleUsageIds: string[]) {
    this._articleUsageIds = articleUsageIds || [];
  }

  public get articleUsageIds(): string[] {
    return this._articleUsageIds;
  }

  public containsArticle(usageId: string): boolean {
    return this._articleUsageIds.indexOf(usageId) >= 0;
  }

  public remove(usageIds: string[]): void {
    this._articleUsageIds = this._articleUsageIds.filter(ArrayUtils.notInArray, usageIds);
  }
}

@Injectable({ providedIn: 'root' })
export class TerminalBlockManager {
  constructor(private containerService: ContainerService, private mountingRailHelper: MountingRailHelper) {}

  public getArticleContainersInTerminalBlock(articleContainer: ArticleContainer, excludedArticleTypes: number[]): ArticleContainer[] {
    if (!articleContainer) {
      return [];
    }

    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!insertArticleHelper) {
      return [];
    }

    return this.getArticleContainersInTerminalBlockInternal(articleContainer, excludedArticleTypes, insertArticleHelper);
  }

  public retrieveTerminalBlocksByUsageIds(
    articleUsageIds: string[],
    excludedArticleTypes: number[],
    insertHelper: InsertArticleHelper
  ): TerminalBlock[] {
    const articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds);
    return this.retrieveTerminalBlocks(articleContainers, excludedArticleTypes, insertHelper);
  }

  public retrieveTerminalBlocks(
    articleContainers: ArticleContainer[],
    excludedArticleTypes: number[],
    insertHelper: InsertArticleHelper
  ): TerminalBlock[] {
    if (!insertHelper) {
      return [];
    }

    const terminalBlocks: TerminalBlock[] = [];
    for (const container of articleContainers) {
      if (!terminalBlocks.some((block) => block.containsArticle(container.articleGUID))) {
        const terminalBlock: TerminalBlock = this.buildTerminalBlock(container, excludedArticleTypes, insertHelper);
        if (terminalBlock) {
          terminalBlocks.push(terminalBlock);
        }
      }
    }
    return terminalBlocks;
  }

  private buildTerminalBlock(articleContainer: ArticleContainer, excludedArticleTypes: number[], insertHelper: InsertArticleHelper): TerminalBlock {
    if (
      !insertHelper ||
      !insertHelper.acceptingIdentity.equals(ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage))
    ) {
      return null;
    }
    if (ArticleUtils.isArticleOfAnyTypeId(articleContainer.article, excludedArticleTypes)) {
      return null;
    }

    const containersInBlock: ArticleContainer[] = this.getArticleContainersInTerminalBlockInternal(
      articleContainer,
      excludedArticleTypes,
      insertHelper
    );
    return new TerminalBlock(ArticleContainerUtils.extractArticleGUIDs(containersInBlock));
  }

  private getArticleContainersInTerminalBlockInternal(
    articleContainer: ArticleContainer,
    excludedArticleTypes: number[],
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    if (!ArticleUtils.isArticleOfAnyTypeId(articleContainer.article, excludedArticleTypes)) {
      const articleContainersInBlock: ArticleContainer[] = [];
      articleContainersInBlock.push(...this.getLeftArticleContainersInBlock(articleContainer, excludedArticleTypes, insertHelper));
      articleContainersInBlock.push(articleContainer);
      articleContainersInBlock.push(...this.getRightArticleContainersInBlock(articleContainer, excludedArticleTypes, insertHelper));
      return articleContainersInBlock;
    } else {
      return [articleContainer];
    }
  }

  private getLeftArticleContainersInBlock(
    articleContainer: ArticleContainer,
    excludedArticleTypes: number[],
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    const leftContainers: ArticleContainer[] = [];

    let targetContainer: ArticleContainer = articleContainer;
    let leftContainer: ArticleContainer;
    while ((leftContainer = this.getLeftArticleContainer(targetContainer, insertHelper))) {
      if (
        !ArticleUtils.isArticleOfAnyTypeId(leftContainer.article, excludedArticleTypes) &&
        this.isLeftNeighborWithoutGap(targetContainer, leftContainer, insertHelper)
      ) {
        leftContainers.push(leftContainer);
        targetContainer = leftContainer;
      } else {
        break;
      }
    }

    return leftContainers.reverse();
  }

  private getLeftArticleContainer(container: ArticleContainer, insertHelper: InsertArticleHelper): ArticleContainer {
    const articleLine: AdvancedLine = insertHelper.getArticleLine(container);
    return !articleLine ? null : insertHelper.getLeftArticleContainerByPoint(articleLine.start);
  }

  private isLeftNeighborWithoutGap(targetContainer: ArticleContainer, leftContainer: ArticleContainer, insertHelper: InsertArticleHelper): boolean {
    const targetLine: AdvancedLine = insertHelper.getArticleLine(targetContainer);
    const leftLine: AdvancedLine = insertHelper.getArticleLine(leftContainer);
    if (!targetLine || !leftLine) {
      return false;
    }

    return targetLine.hasLine(leftLine, false);
  }

  private getRightArticleContainersInBlock(
    articleContainer: ArticleContainer,
    excludedArticleTypes: number[],
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    const rightContainers: ArticleContainer[] = [];

    let targetContainer: ArticleContainer = articleContainer;
    let rightContainer: ArticleContainer;
    while ((rightContainer = this.getRightArticleContainer(targetContainer, insertHelper))) {
      if (
        !ArticleUtils.isArticleOfAnyTypeId(rightContainer.article, excludedArticleTypes) &&
        this.isRightNeighborWithoutGap(targetContainer, rightContainer, insertHelper)
      ) {
        rightContainers.push(rightContainer);
        targetContainer = rightContainer;
      } else {
        break;
      }
    }

    return rightContainers;
  }

  private getRightArticleContainer(container: ArticleContainer, insertHelper: InsertArticleHelper): ArticleContainer {
    const articleLine: AdvancedLine = insertHelper.getArticleLine(container);
    return !articleLine ? null : insertHelper.getRightArticleContainerByPoint(articleLine.end);
  }

  public isRightNeighborWithoutGap(targetContainer: ArticleContainer, rightContainer: ArticleContainer, insertHelper: InsertArticleHelper): boolean {
    const targetLine: AdvancedLine = insertHelper.getArticleLine(targetContainer);
    const rightLine: AdvancedLine = insertHelper.getArticleLine(rightContainer);
    if (!targetLine || !rightLine) {
      return false;
    }

    return targetLine.hasLine(rightLine, false);
  }
}
